import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Badge, Table, Form, InputGroup, Spinner } from 'react-bootstrap';
import { Download, Pencil, Save, Search, XCircle } from 'react-bootstrap-icons';
import { TestDatasetCaseOption, TestDatasetDetail } from '../../types/testData';
import apiClient, { API_ENDPOINTS } from '../../api/config';

interface Props {
    dataset: TestDatasetDetail;
    defaultView?: 'table' | 'json';
    onClose: () => void;
    onDownload: (datasetId: string) => void;
}

function TestDatasetPreviewModal({ dataset: initialDataset, defaultView = 'table', onClose, onDownload }: Props): React.ReactElement {
    const [dataset, setDataset] = useState<TestDatasetDetail>(initialDataset);
    const [filterId, setFilterId] = useState<string>('');
    const [caseSearch, setCaseSearch] = useState('');
    const [isCasePickerOpen, setIsCasePickerOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [localRows, setLocalRows] = useState<any[]>([]);

    useEffect(() => {
        setLocalRows(dataset.rows);
    }, [dataset.rows]);

    const getRowDisplayId = (row: Record<string, unknown>): string => {
        const displayId = String(row.display_id || '').trim();
        if (displayId) return displayId.includes('-STC-') ? displayId.split('-STC-')[0] : displayId;

        const testCaseId = String(row.test_case_id || '').trim();
        if (!testCaseId) return '';
        return testCaseId.includes('-STC-') ? testCaseId.split('-STC-')[0] : testCaseId;
    };

    const rowCountsByDisplayId = useMemo(() => {
        const counts = new Map<string, number>();
        dataset.rows.forEach(row => {
            const displayId = getRowDisplayId(row);
            if (displayId) counts.set(displayId, (counts.get(displayId) || 0) + 1);
        });
        return counts;
    }, [dataset.rows]);

    const testCaseOptions = useMemo<TestDatasetCaseOption[]>(() => {
        if (dataset.test_case_options && dataset.test_case_options.length > 0) {
            return dataset.test_case_options;
        }
        return Array.from(rowCountsByDisplayId.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([displayId, rowCount]) => ({
                display_id: displayId,
                test_key: displayId,
                title: displayId,
                row_count: rowCount,
            }));
    }, [dataset.test_case_options, rowCountsByDisplayId]);

    const filteredRows = useMemo(() => {
        if (!filterId) return [];
        return localRows.filter(row => getRowDisplayId(row) === filterId);
    }, [localRows, filterId]);

    const selectedCase = useMemo(
        () => testCaseOptions.find(option => option.display_id === filterId),
        [filterId, testCaseOptions]
    );

    const filteredCaseOptions = useMemo(() => {
        const query = caseSearch.trim().toLowerCase();
        if (!query) return testCaseOptions;
        return testCaseOptions.filter(option => {
            const haystack = [option.title, option.display_id, option.test_key]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(query);
        });
    }, [caseSearch, testCaseOptions]);

    useEffect(() => {
        if (filterId) return;
        if (testCaseOptions.length === 0) return;
        setFilterId(testCaseOptions[0].display_id);
    }, [filterId, testCaseOptions]);

    const selectedCaseLabel = selectedCase?.title || 'Select Test Case';

    const PREVIEW_LIMIT = 500;
    const previewRows = filteredRows.slice(0, PREVIEW_LIMIT);
    const hasMore = filteredRows.length > PREVIEW_LIMIT;
    const previewColumns = useMemo(() => {
        const columns: string[] = [];
        const preferred = ['test_case_id', 'display_id'];
        const hasMeaningfulValue = (value: unknown): boolean => {
            if (value === null || value === undefined) return false;
            if (typeof value === 'string') return value.trim() !== '';
            return true;
        };

        preferred.forEach((col) => {
            if (previewRows.some((row) => hasMeaningfulValue(row?.[col]))) {
                columns.push(col);
            }
        });

        previewRows.forEach((row) => {
            Object.keys(row || {}).forEach((key) => {
                if (!key || columns.includes(key)) return;
                if (previewRows.some((candidate) => hasMeaningfulValue(candidate?.[key]))) {
                    columns.push(key);
                }
            });
        });

        return columns;
    }, [previewRows]);

    const handleCellChange = (rowIndex: number, col: string, value: string) => {
        const updated = [...localRows];
        // Map filtered index back to global index if needed, but here we use index in filtered list
        // Actually, it's safer to map by a unique key or just work on the full list.
        // Let's find the actual row in localRows to update
        const globalRowIndex = localRows.findIndex(r => r === filteredRows[rowIndex]);
        if (globalRowIndex !== -1) {
            updated[globalRowIndex] = { ...updated[globalRowIndex], [col]: value };
            setLocalRows(updated);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await apiClient.put(
                API_ENDPOINTS.TEST_DATA_ROWS_UPDATE(dataset.project_id, dataset.id),
                { rows: localRows }
            );
            setDataset(res.data);
            setIsEditing(false);
        } catch (err) {
            console.error("Failed to save dataset edits", err);
            alert("Failed to save changes.");
        } finally {
            setSaving(false);
        }
    };

    const handleDownloadSelectedCaseCsv = () => {
        if (!selectedCase || previewColumns.length === 0) return;

        const escapeCsv = (value: unknown): string => {
            const text = String(value ?? '');
            if (/[",\n]/.test(text)) {
                return `"${text.replace(/"/g, '""')}"`;
            }
            return text;
        };

        const lines = [
            previewColumns.map(escapeCsv).join(','),
            ...filteredRows.map((row) => previewColumns.map((col) => escapeCsv(row?.[col])).join(',')),
        ];
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const baseName = `${dataset.name}_${selectedCase.display_id}`.replace(/[^\w.-]+/g, '_');
        link.href = url;
        link.download = `${baseName}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Modal show onHide={onClose} size="xl" centered className="test-data-preview-modal">
            <Modal.Header style={{ borderBottom: '1px solid #e8ecf0', display: 'block', padding: '16px 24px' }}>
                <div className="d-flex justify-content-between align-items-start w-100 mb-3">
                    <div>
                        <Modal.Title style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
                            {dataset.name}
                        </Modal.Title>
                        <div className="d-flex gap-2 mt-2 align-items-center">
                            <Badge bg="secondary-subtle" text="secondary" style={{ fontWeight: 600, fontSize: '0.75rem', border: '1px solid #e2e8f0' }}>
                                {dataset.row_count} total rows
                            </Badge>
                            <Badge
                                bg={dataset.source === 'generated' || dataset.source === 'synthetic_generation' ? 'primary-subtle' : 'secondary-subtle'}
                                text={dataset.source === 'generated' || dataset.source === 'synthetic_generation' ? 'primary' : 'secondary'}
                                style={{ fontWeight: 600, fontSize: '0.75rem', border: '1px solid currentColor', opacity: 0.8 }}
                            >
                                {dataset.source}
                            </Badge>
                        </div>
                    </div>
                    <button onClick={onClose} className="btn-close shadow-none" aria-label="Close" />
                </div>

                <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded-3 border">
                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                        <div style={{ position: 'relative', width: 420, maxWidth: '52vw' }}>
                            <InputGroup size="sm">
                                <InputGroup.Text className="bg-white border-end-0 text-muted">
                                    <Search size={14} />
                                </InputGroup.Text>
                                <Form.Control
                                    value={isCasePickerOpen ? caseSearch : selectedCaseLabel}
                                    onFocus={() => {
                                        setCaseSearch('');
                                        setIsCasePickerOpen(true);
                                    }}
                                    onClick={() => {
                                        if (!isCasePickerOpen) {
                                            setCaseSearch('');
                                        }
                                        setIsCasePickerOpen(true);
                                    }}
                                    onChange={(e) => {
                                        setCaseSearch(e.target.value);
                                        setIsCasePickerOpen(true);
                                    }}
                                    onBlur={() => window.setTimeout(() => setIsCasePickerOpen(false), 120)}
                                    placeholder="Search test case title or ID"
                                    className="border-start-0 shadow-none"
                                    aria-label="Search test cases"
                                    style={{ fontSize: '0.85rem' }}
                                />
                            </InputGroup>
                            {isCasePickerOpen && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 6px)',
                                        left: 0,
                                        right: 0,
                                        zIndex: 30,
                                        background: '#ffffff',
                                        border: '1px solid #dbe3ef',
                                        borderRadius: 8,
                                        boxShadow: '0 14px 32px rgba(15, 23, 42, 0.16)',
                                        maxHeight: 280,
                                        overflowY: 'auto',
                                        padding: 6,
                                    }}
                                >
                                    {filteredCaseOptions.length === 0 ? (
                                        <div className="small text-muted px-2 py-3">No matching test cases</div>
                                    ) : (
                                        filteredCaseOptions.map(option => (
                                            <button
                                                key={option.display_id}
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => {
                                                    setFilterId(option.display_id);
                                                    setCaseSearch('');
                                                    setIsCasePickerOpen(false);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    border: 'none',
                                                    background: filterId === option.display_id ? '#eff6ff' : 'transparent',
                                                    borderRadius: 6,
                                                    padding: '9px 10px',
                                                    textAlign: 'left',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 12,
                                                    color: '#0f172a',
                                                }}
                                            >
                                                <span style={{ minWidth: 0 }}>
                                                    <span
                                                        style={{
                                                            display: 'block',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            fontWeight: 600,
                                                            fontSize: '0.86rem',
                                                        }}
                                                        title={option.title}
                                                    >
                                                        {option.title}
                                                    </span>
                                                    <span className="text-muted" style={{ display: 'block', fontSize: '0.75rem' }}>
                                                        {option.display_id}
                                                    </span>
                                                </span>
                                                <Badge bg="light" text="secondary" style={{ border: '1px solid #e2e8f0', flexShrink: 0 }}>
                                                    {option.row_count} rows
                                                </Badge>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                        {filterId && (
                            <span className="small text-muted fw-bold">
                                {filteredRows.length} variants showing
                            </span>
                        )}
                    </div>

                    <div className="d-flex gap-2">
                        {isEditing ? (
                            <>
                                <Button variant="outline-danger" size="sm" onClick={() => { setIsEditing(false); setLocalRows(dataset.rows); }} disabled={saving}>
                                    <XCircle size={14} className="me-1" /> Cancel
                                </Button>
                                <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
                                    {saving ? <Spinner animation="border" size="sm" /> : <><Save size={14} className="me-1" /> Save Changes</>}
                                </Button>
                            </>
                        ) : (
                            <Button variant="outline-primary" size="sm" onClick={() => setIsEditing(true)}>
                                <Pencil size={14} className="me-1" /> Edit Mode
                            </Button>
                        )}
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body style={{ padding: 0 }}>
                {defaultView === 'json' ? (
                    <div style={{ padding: '16px', background: '#1e293b', color: '#f1f5f9', maxHeight: '65vh', overflow: 'auto' }}>
                        <pre style={{ margin: 0, fontSize: '0.85rem', fontFamily: 'monospace' }}>
                            {JSON.stringify(filteredRows, null, 2)}
                        </pre>
                    </div>
                ) : (
                    !filterId ? (
                        <p style={{ padding: '48px', color: '#64748b', textAlign: 'center', fontSize: '0.9rem' }}>
                            Select a test case to preview its generated variants.
                        </p>
                    ) : previewColumns.length === 0 ? (
                        <p style={{ padding: '48px', color: '#64748b', textAlign: 'center', fontSize: '0.9rem' }}>
                            {selectedCase?.row_count === 0
                                ? 'This test case does not require synthetic data.'
                                : 'No variant rows are available for the selected test case.'}
                        </p>
                    ) : (
                        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
                            <Table bordered hover size="sm" className="mb-0" style={{ fontSize: '0.85rem', borderCollapse: 'separate', borderSpacing: 0 }}>
                                <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8fafc' }}>
                                    <tr>
                                        {previewColumns.map(col => (
                                            <th
                                                key={col}
                                                style={{
                                                    padding: '12px 16px',
                                                    fontWeight: 600,
                                                    color: '#475569',
                                                    whiteSpace: 'nowrap',
                                                    backgroundColor: '#f8fafc',
                                                    borderBottom: '2px solid #e2e8f0',
                                                    borderRight: '1px solid #e2e8f0',
                                                }}
                                            >
                                                {col}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                    <tbody>
                                        {previewRows.map((row, i) => (
                                            <tr key={i} style={{ transition: 'background 0.2s' }}>
                                                {previewColumns.map(col => (
                                                    <td
                                                        key={col}
                                                        style={{
                                                        padding: isEditing ? '4px 8px' : '10px 16px',
                                                        color: '#1e293b',
                                                        borderColor: '#f1f5f9',
                                                        maxWidth: 400,
                                                        minWidth: 120,
                                                        backgroundColor: isEditing ? '#fff' : 'inherit'
                                                    }}
                                                >
                                                    {isEditing ? (
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={String(row[col] ?? '')}
                                                            onChange={(e) => handleCellChange(i, col, e.target.value)}
                                                            className="border-0 shadow-none bg-transparent p-1"
                                                            style={{ fontSize: '0.85rem' }}
                                                        />
                                                    ) : (
                                                        <div className="text-truncate" title={String(row[col] ?? '')}>
                                                            {String(row[col] ?? '')}
                                                        </div>
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {hasMore && (
                                <div className="p-3 text-center border-top bg-light-subtle small text-muted">
                                    Showing first {PREVIEW_LIMIT} rows. Download full CSV to see all {filteredRows.length} matching rows.
                                </div>
                            )}
                        </div>
                    )
                )}
            </Modal.Body>

            <Modal.Footer className="bg-light-subtle border-top py-3">
                <Button variant="link" className="text-decoration-none text-muted me-auto" onClick={onClose}>
                    Close Preview
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                        if (filterId) {
                            handleDownloadSelectedCaseCsv();
                            return;
                        }
                        onDownload(dataset.id);
                    }}
                    className="px-3"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}
                >
                    <Download size={15} />
                    {filterId ? 'Download Selected CSV' : 'Download CSV'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default TestDatasetPreviewModal;
